import { useCallback } from 'react';

import { getPath, pathToUrl, routeNames } from '../../../shared/helpers/routes';
import {
  trackButtonClickEvent,
  trackClickEvent,
} from '../../../services/tracking/pulseEvents';
import { useVendor } from '../../VendorPovider/VendorPovider';
import { useAlgoliaAnalytics } from '../../AlgoliaAnalytics/AlgoliaAnalytics';
import { getCurrentPageViewData } from '../../../services/tracking/helpers/pulseOrigin';

import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';

export const useCategoryItemTracking = ({
  elementType,
  header = '',
  pageComponentIndex,
  headerButtonLabel,
  skin = 'vertical',
}: {
  elementType: string;
  header?: string;
  headerButtonLabel?: string;
  pageComponentIndex?: number;
  skin?: string;
}) => {
  const { vendor } = useVendor();
  const { trackHitClick } = useAlgoliaAnalytics();

  const trackCategoryClick = useCallback(
    (category: SvpCategoryAdditional, columnIndex: number) => {
      trackHitClick({ objectID: String(category.id) });

      const parent = {
        elementType,
        name: header,
        row: pageComponentIndex,
        skin,
      };

      const url = pathToUrl(
        getPath(routeNames.category, {
          vendor,
          category,
        })
      );

      const object = {
        id: category.id,
        title: category.title,
        elementType: 'Category',
        position: columnIndex,
        url: url,
      };

      const target = {
        id: category.id,
        title: category.title,
        url: url,
        type: 'Category',
      };

      trackClickEvent(parent, object, target);
    },
    [trackHitClick, elementType, header, pageComponentIndex, skin, vendor]
  );

  const trackHeaderButtonClick = useCallback(() => {
    if (!headerButtonLabel) {
      return;
    }

    const currentPageViewData = getCurrentPageViewData();

    const parent = {
      elementType,
      name: header,
      row: pageComponentIndex,
    };

    const target = {
      elementType: 'ComponentPopup',
      name: header,
      url: currentPageViewData.url,
    };

    const button = {
      elementType: 'SeeAll',
      title: headerButtonLabel,
      position: 'slider-header',
    };

    trackButtonClickEvent(button, target, parent);
  }, [elementType, header, pageComponentIndex, headerButtonLabel]);

  return {
    trackCategoryClick,
    trackHeaderButtonClick,
  };
};
